import React, { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, MessageCircle, Phone, StickyNote } from "lucide-react";
import { errore, fmtTelefono, TipoSoggetto } from "./crmComuni";

// Sprint CRM-2A — chiama, WhatsApp, email con un clic; ogni interazione appartiene a UNA opportunità
export const INTERAZIONI = {
  nota: { label: "Nota", icon: StickyNote }, chiamata: { label: "Chiamata", icon: Phone },
  whatsapp: { label: "WhatsApp", icon: MessageCircle }, email: { label: "Email", icon: Mail },
};
const AZIONE = { chiamata: "Chiama", whatsapp: "WhatsApp", email: "Email" };

export const linkRecapito = (tipo, v) => (tipo === "chiamata" ? `tel:${v}`
  : tipo === "whatsapp" ? `https://wa.me/${v.replace(/\D/g, "")}` : `mailto:${v}`);

function Ancora({ tipo, valore, onAperto, className, testid, children, title }) {
  const fuori = tipo === "whatsapp";
  return (
    <a href={linkRecapito(tipo, valore)} target={fuori ? "_blank" : undefined} rel={fuori ? "noopener noreferrer" : undefined}
      onClick={(e) => { e.stopPropagation(); onAperto(tipo); }} className={className} title={title} data-testid={testid}>{children}</a>
  );
}

// Sprint CRM-5 — telefonare in azzurro, WhatsApp nel suo verde (leggibile), email nel blu del gestionale
const TONDO = "inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 transition-colors";
const COLORE = { chiamata: "text-sky-500", whatsapp: "text-[#128C7E]", email: "text-[#0F4C81]" };
const mostra = (tipo, v) => (tipo === "email" ? v : fmtTelefono(v));

function Pulsante({ tipo, valori, onAperto, prefix }) {
  const Icon = INTERAZIONI[tipo].icon;
  if (valori.length === 1) {
    return <Ancora tipo={tipo} valore={valori[0]} onAperto={onAperto} className={`${TONDO} ${COLORE[tipo]}`} title={`${AZIONE[tipo]} ${mostra(tipo, valori[0])}`}
      testid={`${prefix}-${tipo}`}><Icon className="h-3.5 w-3.5" /></Ancora>;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={`${TONDO} ${COLORE[tipo]}`} title={AZIONE[tipo]} onClick={(e) => e.stopPropagation()} data-testid={`${prefix}-${tipo}`}>
          <Icon className="h-3.5 w-3.5" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
        {valori.map((v, i) => (
          <DropdownMenuItem key={v} asChild>
            <Ancora tipo={tipo} valore={v} onAperto={onAperto} className="cursor-pointer" testid={`${prefix}-${tipo}-${i}`}>{mostra(tipo, v)}</Ancora>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// opportunita: quelle a cui si può collegare l'interazione (una sola = già scelta; nessuna = niente registrazione)
export default function Recapiti({ contatto, opportunita, prefix, onRegistrata, tipi = ["chiamata", "whatsapp", "email"] }) {
  const [tipo, setTipo] = useState(null);
  const tel = contatto?.telefoni || [];
  const mail = contatto?.email || [];
  if (!tel.length && !mail.length) return null;
  const aperto = (t) => { if (opportunita.length) setTipo(t); };
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()} data-testid={prefix}>
      {!!tel.length && tipi.includes("chiamata") && <Pulsante tipo="chiamata" valori={tel} onAperto={aperto} prefix={prefix} />}
      {!!tel.length && tipi.includes("whatsapp") && <Pulsante tipo="whatsapp" valori={tel} onAperto={aperto} prefix={prefix} />}
      {!!mail.length && tipi.includes("email") && <Pulsante tipo="email" valori={mail} onAperto={aperto} prefix={prefix} />}
      {tipo && <RegistraInterazione tipo={tipo} opportunita={opportunita} onChiudi={() => setTipo(null)} onRegistrata={onRegistrata} />}
    </span>
  );
}

export function RegistraInterazione({ tipo, opportunita, onChiudi, onRegistrata }) {
  const [oid, setOid] = useState(opportunita.length === 1 ? opportunita[0].id : "");
  const [esito, setEsito] = useState("");
  const [busy, setBusy] = useState(false);
  const registra = async () => {
    setBusy(true);
    try {
      await api.post(`/crm/opportunita/${oid}/interazioni`, { tipo, testo: esito });
      toast.success("Interazione registrata"); onChiudi(); onRegistrata?.();
    } catch (e) { toast.error(errore(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="crm-registra-dialog">
        <DialogHeader><DialogTitle>Registrare l'interazione?</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm" data-testid="crm-registra-tipo">Tipo: <b>{INTERAZIONI[tipo].label}</b></div>
          {opportunita.length > 1 && <div><Label>Opportunità</Label>
            <Select value={oid} onValueChange={setOid}>
              <SelectTrigger data-testid="crm-registra-opportunita"><SelectValue placeholder="Scegli l'opportunità" /></SelectTrigger>
              <SelectContent>{opportunita.map((o) => <SelectItem key={o.id} value={o.id}>{o.descrizione}</SelectItem>)}</SelectContent>
            </Select></div>}
          <div><Label>Esito (facoltativo)</Label>
            <Textarea value={esito} onChange={(e) => setEsito(e.target.value)} data-testid="crm-registra-esito" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi} data-testid="crm-registra-no">Non registrare</Button>
          <Button onClick={registra} disabled={!oid || busy} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-registra-si">Registra</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Sprint CRM-2A.1 — riquadro del contatto: tutti i telefoni e tutte le email per esteso, ognuno con le sue icone
export function RiquadroContatto({ c, opportunita, onRegistrata, prefix, titolo }) {
  if (!c) return <div className="text-sm">—</div>;
  return (
    <div className="space-y-1 text-sm" data-testid={prefix}>
      <div className="flex items-center gap-2">{titolo}<TipoSoggetto tipo={c.tipo_soggetto} testid={`${prefix}-tipo`} /></div>
      {(c.telefoni || []).map((t, i) => (
        <div key={t} className="flex items-center gap-2" data-testid={`${prefix}-tel-${i}`}><Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" /><span className="font-mono">{fmtTelefono(t)}</span>
          <Recapiti contatto={{ telefoni: [t] }} opportunita={opportunita} prefix={`${prefix}-tel-${i}-az`} onRegistrata={onRegistrata} tipi={["chiamata", "whatsapp"]} /></div>
      ))}
      {(c.email || []).map((e, i) => (
        <div key={e} className="flex items-center gap-2" data-testid={`${prefix}-email-${i}`}><Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" /><span>{e}</span>
          <Recapiti contatto={{ email: [e] }} opportunita={opportunita} prefix={`${prefix}-email-${i}-az`} onRegistrata={onRegistrata} /></div>
      ))}
      {c.altri_recapiti && <div className="text-slate-600" data-testid={`${prefix}-altri`}>{c.altri_recapiti}</div>}
      <div className="text-slate-600" data-testid={`${prefix}-luogo`}>{[c.comune, c.provincia && `(${c.provincia})`].filter(Boolean).join(" ") || "—"}</div>
    </div>
  );
}
